/**
 * Prairie Malt — Biggar, Saskatchewan. Only the house-brand malts malted
 * there; the imported brands they distribute (Pauls, Salzgitter, Les
 * Maltiers, …) are not Prairie products and are not listed here.
 *
 * Colour is published in ASBC / °L. `colorLovibond` is the mid-point of the
 * published band and EBC is derived with `EBC = 2.65 × °L − 1.2`. Extract is
 * a published fine-grind minimum, moisture a published maximum, diastatic
 * power a published Lintner minimum (or a single figure where they print
 * one). Where every stored number is the maker's, the row is `spec-sheet`.
 * Crystal and roasted rows take DP 0 by physics.
 *
 * Caramel 30 / 60 / 80 and Distillers Malt publish colour on the catalogue
 * listing; extract, moisture and (for Distillers) diastatic power are
 * class-typical from the sibling SKUs we have sheets for, so those four are
 * `representative`.
 *
 * @see https://www.prairiemalt.com/products
 */

import type { Fermentable } from '../../schema/fermentable';

/** Prairie Malt house-brand malts. @see Fermentable */
export const prairieMaltFermentables: readonly Fermentable[] = [
  {
    id: 'prairie-two-row-pale',
    name: 'Two-Row Pale',
    maker: 'Prairie Malt',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2,
    colorEbc: 4.1,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The Biggar workhorse: Copeland, Synergy, Connect and Fraser, malted pale and consistent. Neutral, high-enzyme two-row for anything that does not need the malt to have an opinion.',
    tags: ['base', 'two-row', 'canadian', 'high-enzyme'],
    url: 'https://prairiemalt.com/products/prairie-malt-two-row-pale',
    provenance: 'spec-sheet',
  },
  {
    id: 'prairie-pils',
    name: 'Prairie Pils',
    maker: 'Prairie Malt',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.8,
    colorEbc: 3.6,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A paler, cleaner sister of the two-row: fresh flour and white cracker rather than biscuit. Built as a pilsner and light-lager base that still has North American enzyme behind it.',
    tags: ['base', 'lager', 'pilsner', 'canadian'],
    url: 'https://prairiemalt.com/products/prairie-malt-prairie-pils',
    provenance: 'spec-sheet',
  },
  {
    id: 'prairie-select',
    name: 'Prairie Select',
    maker: 'Prairie Malt',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest, lowest-protein base in the range. The one to reach for when a pilsner or light lager has to stay straw-yellow and the protein haze of a standard two-row would show.',
    tags: ['base', 'lager', 'pilsner', 'canadian', 'low-protein'],
    url: 'https://prairiemalt.com/products/prairie-malt-prairie-select',
    provenance: 'spec-sheet',
  },
  {
    id: 'prairie-special-pale',
    name: 'Special Pale',
    maker: 'Prairie Malt',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 3.5,
    colorEbc: 8.1,
    diastaticPowerLintner: 90,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The two-row kilned longer: freshly baked bread rather than flour, with more color and body. An ale base with something to say, still pale enough for IPA.',
    tags: ['base', 'pale-ale', 'canadian', 'bread'],
    url: 'https://prairiemalt.com/products/prairie-malt-special-pale',
    provenance: 'spec-sheet',
  },
  {
    id: 'prairie-white-wheat',
    name: 'White Wheat',
    maker: 'Prairie Malt',
    origin: 'Canada',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 4.5,
    colorLovibond: 2.9,
    colorEbc: 6.5,
    diastaticPowerLintner: 120,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted white wheat, doughy and faintly honeyed. High extract and plenty of enzyme; the maker caps it at half the grist, which is also where the lauter usually asks for hulls.',
    tags: ['base', 'wheat', 'canadian', 'head-retention'],
    url: 'https://prairiemalt.com/products/prairie-malt-malted-white-wheat',
    provenance: 'spec-sheet',
  },
  {
    id: 'prairie-loxless',
    name: 'Loxless Malt',
    maker: 'Prairie Malt',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2,
    colorEbc: 4.1,
    diastaticPowerLintner: 110,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Two-row from a lipoxygenase-null barley, so it cannot make the trans-2-nonenal that turns packaged beer to cardboard. Brews like the standard pale; the point is shelf life, not flavor.',
    tags: ['base', 'two-row', 'canadian', 'lox-null', 'stability'],
    url: 'https://prairiemalt.com/products/prairie-malt-loxless-malt',
    provenance: 'spec-sheet',
  },
  {
    id: 'prairie-munich-10',
    name: 'Munich 10',
    maker: 'Prairie Malt',
    origin: 'Canada',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 11,
    colorEbc: 28,
    diastaticPowerLintner: 45,
    suggestedMaxPercent: 40,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A darker Munich with a caramel-toast profile, sitting between a light Munich and a caramel 10. Five to forty percent for festbier, amber lager and anything that wants malt without crystal sweetness.',
    tags: ['kilned', 'munich', 'canadian'],
    url: 'https://prairiemalt.com/products/prairie-malt-munich-10',
    provenance: 'spec-sheet',
  },
  {
    id: 'prairie-caramel-20',
    name: 'Caramel 20',
    maker: 'Prairie Malt',
    origin: 'Canada',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 4.5,
    colorLovibond: 20,
    colorEbc: 51.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale caramel: golden syrup, dried fruit and nougat. Up to 20 % for a gold ale or a pale that wants a kiss of color without toffee taking over.',
    tags: ['crystal', 'caramel', 'canadian', 'light'],
    url: 'https://prairiemalt.com/products/prairie-malt-caramel-20',
    provenance: 'spec-sheet',
  },
  {
    id: 'prairie-caramel-30',
    name: 'Caramel 30',
    maker: 'Prairie Malt',
    origin: 'Canada',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 4.5,
    colorLovibond: 30,
    colorEbc: 78.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The middle of the pale caramels. Clean caramel sweetness and an amber cast; five to fifteen percent is the everyday dose in pale ale and amber ale.',
    tags: ['crystal', 'caramel', 'canadian'],
    provenance: 'representative',
  },
  {
    id: 'prairie-caramel-40',
    name: 'Caramel 40',
    maker: 'Prairie Malt',
    origin: 'Canada',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 4.5,
    colorLovibond: 40,
    colorEbc: 104.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Toasty caramel with biscuit, toffee and an almond finish. Five to twenty percent for amber ale, red ale and anything that wants a nutty caramel middle.',
    tags: ['crystal', 'caramel', 'canadian', 'toffee'],
    url: 'https://prairiemalt.com/products/prairie-malt-caramel-40',
    provenance: 'spec-sheet',
  },
  {
    id: 'prairie-caramel-60',
    name: 'Caramel 60',
    maker: 'Prairie Malt',
    origin: 'Canada',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 4.5,
    colorLovibond: 60,
    colorEbc: 157.8,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The standard medium caramel: toast, biscuit and a proper amber-red. Five to ten percent in bitter, red ale and brown ale before the sweetness stacks up.',
    tags: ['crystal', 'caramel', 'canadian'],
    provenance: 'representative',
  },
  {
    id: 'prairie-caramel-80',
    name: 'Caramel 80',
    maker: 'Prairie Malt',
    origin: 'Canada',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 70,
    moisturePercent: 4.5,
    colorLovibond: 77.5,
    colorEbc: 204.2,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dark caramel, heading toward burnt sugar and dried fruit. A few percent colors and sweetens a brown ale, a porter or a strong ale.',
    tags: ['crystal', 'caramel', 'canadian', 'dark'],
    provenance: 'representative',
  },
  {
    id: 'prairie-distillers',
    name: 'Distillers Malt',
    maker: 'Prairie Malt',
    origin: 'Canada',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2,
    colorEbc: 4.1,
    diastaticPowerLintner: 160,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'High-enzyme pale malt for grain-whisky and rye mashes. Brews like the two-row with more converting power left over for unmalted corn or rye.',
    tags: ['base', 'two-row', 'canadian', 'distilling', 'high-enzyme'],
    provenance: 'representative',
  },
];
